/**
 * 跨窗口状态同步工具
 *
 * 场景：同一浏览器同域名下同时打开多个窗口（标签页）时，
 * 任一窗口发生登录、退出登录（含登录失效）、会员状态更新后，
 * 通知其他窗口刷新页面，保证登录态与会员数据同步。
 *
 * 实现：优先使用 BroadcastChannel，浏览器不支持时降级为 localStorage storage 事件；
 * 两种通道均不会把消息回发给发送方窗口自身，不影响发送方原有刷新逻辑。
 */

export type CrossTabSyncType = 'login' | 'logout' | 'memberUpdate'

const CHANNEL_NAME = 'yt-pte-cross-tab-sync'
/** BroadcastChannel 不可用时的降级消息 key */
const FALLBACK_KEY = '__cross_tab_sync_event__'

interface CrossTabMessage {
  type: CrossTabSyncType
  ts: number
  nonce: number
}

const VALID_TYPES: CrossTabSyncType[] = ['login', 'logout', 'memberUpdate']
const hasBroadcastChannel = typeof BroadcastChannel !== 'undefined'

let channel: BroadcastChannel | null = null
let initialized = false
/** 防止多条消息连续到达时重复触发刷新 */
let reloading = false

/** 收到其他窗口的状态变化消息后刷新本窗口 */
const reloadToSync = () => {
  if (reloading) return
  reloading = true
  console.log('[cross-tab] 检测到其他窗口的登录态/会员状态变化，刷新页面同步数据')
  window.location.reload()
}

const ensureChannel = () => {
  if (!hasBroadcastChannel) return null
  if (!channel) channel = new BroadcastChannel(CHANNEL_NAME)
  return channel
}

/**
 * 通知其他窗口刷新（不会通知当前窗口自身）
 * @param type 变化类型：login 登录成功 / logout 退出登录或登录失效 / memberUpdate 会员状态更新
 */
export function notifyCrossTab(type: CrossTabSyncType) {
  if (hasBroadcastChannel) {
    ensureChannel()?.postMessage({ type, ts: Date.now(), nonce: Math.random() } as CrossTabMessage)
    return
  }
  // 降级：写入 localStorage 触发其他窗口的 storage 事件（值需保证唯一，否则同值写入不会触发事件）
  try {
    localStorage.setItem(
      FALLBACK_KEY,
      JSON.stringify({ type, ts: Date.now(), nonce: Math.random() } as CrossTabMessage)
    )
  } catch {
    // localStorage 不可用（如隐私模式）时忽略
  }
}

/** 在应用入口（App.vue）调用一次，监听其他窗口的同步消息并刷新页面 */
export function initCrossTabSync() {
  if (initialized) return
  initialized = true

  if (hasBroadcastChannel) {
    const ch = ensureChannel()
    if (ch) {
      ch.onmessage = (e: MessageEvent<CrossTabMessage>) => {
        if (VALID_TYPES.includes(e.data?.type)) reloadToSync()
      }
      return
    }
  }

  // 降级：监听 storage 事件（只处理本工具写入的消息）
  window.addEventListener('storage', (e) => {
    if (e.key !== FALLBACK_KEY || !e.newValue) return
    try {
      const msg = JSON.parse(e.newValue) as CrossTabMessage
      if (VALID_TYPES.includes(msg.type)) reloadToSync()
    } catch {
      // 非法消息忽略
    }
  })
}
